import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Shield, Users } from "lucide-react";
import { EMPTY_USERS, TOAST_NO_PERMISSION, TOAST_SAVE_ERROR, TOAST_REMOVE_ERROR, TOAST_UPDATED, TOAST_ADDED, TOAST_SAVED, TOAST_REQUIRED, TOAST_REMOVED, TOAST_LOAD_ERROR, PLH_FULL_NAME, PLH_EMAIL, PLH_USER_NAME, PLH_OPTIONAL, PLH_MIN_PASSWORD, TIP_EDIT, TIP_DELETE, TIP_REMOVE } from "../../lib/constants";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { getUsers, getRoles, createUser, updateUser, deleteUser } from "../../lib/api";

export default function AdminUsers({ readOnly = false }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteUid, setDeleteUid] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", name: "", role_uid: "" });

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        getUsers(),
        getRoles(),
      ]);
      setUsers(Array.isArray(usersRes) ? usersRes : []);
      setRoles(Array.isArray(rolesRes) ? rolesRes : []);
    } catch {
      toast.error(TOAST_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    if (!formData.email || (!editUser && !formData.password)) {
      toast.error(TOAST_REQUIRED);
      return;
    }
    if (!formData.role_uid) {
      toast.error(TOAST_REQUIRED);
      return;
    }
    setSaving(true);
    try {
      if (editUser) {
        await updateUser(editUser.uid, { email: formData.email, name: formData.name, role_uid: formData.role_uid, ...(formData.password ? { password: formData.password } : {}) });
        toast.success(TOAST_UPDATED);
      } else {
        await createUser({ ...formData, active: 1 });
        toast.success(TOAST_ADDED);
      }
      setFormOpen(false);
      setEditUser(null);
      setFormData({ email: "", password: "", name: "", role_uid: "" });
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    try {
      await deleteUser(deleteUid);
      toast.success(TOAST_REMOVED);
      setDeleteUid(null);
      await load();
    } catch {
      toast.error(TOAST_REMOVE_ERROR);
    }
  }

  function openEdit(u) {
    const role = roles.find(r => r.name === u.role);
    setEditUser(u);
    setFormData({ email: u.email, password: "", name: u.name || "", role_uid: role?.uid || "" });
    setFormOpen(true);
  }

  function openCreate() {
    setEditUser(null);
    setFormData({ email: "", password: "", name: "", role_uid: "" });
    setFormOpen(true);
  }

  return (
    <div data-id="admin-users" className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl text-softblack">Utilizadores</h2>
          <p className="text-warmgray text-sm mt-1">Gerencie os acessos ao painel</p>
        </div>
        {!readOnly && (
          <button onClick={openCreate} className="btn-primary flex items-center gap-2 self-start sm:self-auto">
            <Plus size={14} /> Novo
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-champagne" size={24} /></div>
      ) : users.length === 0 ? (
        <div className="bg-white border border-fendi rounded-xl py-12 text-center text-warmgray text-sm">{EMPTY_USERS}</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white border border-fendi rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-fendi/60">
                  <th className="text-left px-6 py-3 text-[10px] tracking-[0.28em] uppercase text-warmgray font-normal">Nome</th>
                  <th className="text-left px-6 py-3 text-[10px] tracking-[0.28em] uppercase text-warmgray font-normal">Email</th>
                  <th className="text-left px-6 py-3 text-[10px] tracking-[0.28em] uppercase text-warmgray font-normal">Função</th>
                  <th className="text-left px-6 py-3 text-[10px] tracking-[0.28em] uppercase text-warmgray font-normal">Estado</th>
                  <th className="text-right px-6 py-3 text-[10px] tracking-[0.28em] uppercase text-warmgray font-normal">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.uid} className="border-b border-fendi/30 last:border-0 hover:bg-offwhite/50">
                    <td className="px-6 py-4 text-sm text-softblack">{u.name || "—"}</td>
                    <td className="px-6 py-4 text-sm text-warmgray">{u.email}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider" style={{ backgroundColor: u.role_color ? `${u.role_color}20` : "#f0f0f0", color: u.role_color || "#888" }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: u.role_color || "#888" }} />
                        {u.role_label || u.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] uppercase tracking-wider ${u.active ? "text-green-600" : "text-destructive"}`}>
                        {u.active ? "Ativo" : "Inativo"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {!readOnly && (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(u)} className="p-1.5 hover:bg-fendi/20 rounded transition-colors" title={TIP_EDIT}>
                            <Pencil size={14} className="text-warmgray" />
                          </button>
                          <button onClick={() => setDeleteUid(u.uid)} className="p-1.5 hover:bg-destructive/10 rounded transition-colors" title={TIP_REMOVE}>
                            <Trash2 size={14} className="text-destructive" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {users.map(u => (
              <div key={u.uid} className="bg-white border border-fendi rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-softblack truncate">{u.name || "—"}</p>
                    <p className="text-xs text-warmgray truncate mt-0.5">{u.email}</p>
                  </div>
                  {!readOnly && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => openEdit(u)} className="p-2 hover:bg-fendi/20 rounded transition-colors" title={TIP_EDIT}>
                        <Pencil size={14} className="text-warmgray" />
                      </button>
                      <button onClick={() => setDeleteUid(u.uid)} className="p-2 hover:bg-destructive/10 rounded transition-colors" title={TIP_REMOVE}>
                        <Trash2 size={14} className="text-destructive" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-fendi/20">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider" style={{ backgroundColor: u.role_color ? `${u.role_color}20` : "#f0f0f0", color: u.role_color || "#888" }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: u.role_color || "#888" }} />
                    {u.role_label || u.role}
                  </span>
                  <span className={`text-[10px] uppercase tracking-wider ${u.active ? "text-green-600" : "text-destructive"}`}>
                    {u.active ? "Ativo" : "Inativo"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create/Edit Dialog */}
      <AlertDialog open={formOpen} onOpenChange={setFormOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{editUser ? "Editar Utilizador" : "Novo Utilizador"}</AlertDialogTitle>
            <AlertDialogDescription>Preencha os dados do usuário.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <label className="block">
              <span className="eyebrow">Nome</span>
              <input type="text" className="editorial-field mt-2" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder={PLH_FULL_NAME} />
            </label>
            <label className="block">
              <span className="eyebrow">Email</span>
              <input type="email" className="editorial-field mt-2" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder={PLH_EMAIL} />
            </label>
            <label className="block">
              <span className="eyebrow">{editUser ? "Nova palavra-passe (deixe vazio para manter)" : "Palavra-passe"}</span>
              <input type="password" className="editorial-field mt-2" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder={editUser ? PLH_OPTIONAL : PLH_MIN_PASSWORD} />
            </label>
            <label className="block">
              <span className="eyebrow">Role</span>
              <select className="editorial-field mt-2" value={formData.role_uid} onChange={e => setFormData({ ...formData, role_uid: e.target.value })}>
                <option value="">Selecione um role</option>
                {roles.map(r => (
                  <option key={r.uid} value={r.uid}>{r.label || r.name}</option>
                ))}
              </select>
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            {!readOnly && (
              <AlertDialogAction onClick={handleSave} disabled={saving} className="bg-softblack hover:bg-softblack/90 disabled:opacity-60">
                {saving ? "A guardar..." : editUser ? "Atualizar" : "Criar"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteUid} onOpenChange={() => setDeleteUid(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover usuário?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            {!readOnly && (
              <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Remover</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
